import React, { useState } from "react";
import { X } from "lucide-react";
import { RxCross2 } from "react-icons/rx";

const defaultFormData = {
  slug: "",
  title: "",
  tagline: "",
  shortDesc: "",
  heroImage: "",
  icon: "",
  color: "",

  overview: {
    heading: "",
    body: "",
  },

  stats: [],

  features: [],

  process: [],

  benefits: [],

  industries: [],

  technologies: [],

  faq: [],
};

const ServiceFormModal = ({ mode, service, onClose, onSave, apiBaseUrl }) => {
  const [formData, setFormData] = useState(service || defaultFormData);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);

  const handleFormChange = (e) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? e.target.checked : value,
    }));
  };

  const handleOverviewChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      overview: {
        ...prev.overview,
        [field]: value,
      },
    }));
  };

  // ---------------------------
  // Generic Array Handlers
  // ---------------------------

  const handleArrayFieldChange = (fieldName, index, value) => {
    setFormData((prev) => {
      const newArray = [...prev[fieldName]];
      newArray[index] = value;

      return {
        ...prev,
        [fieldName]: newArray,
      };
    });
  };

  const handleAddArrayField = (fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: [...prev[fieldName], ""],
    }));
  };

  const handleRemoveArrayField = (fieldName, index) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index),
    }));
  };

  // ---------------------------
  // Statistics Handlers
  // ---------------------------

  const handleStatChange = (index, field, value) => {
    setFormData((prev) => {
      const newStats = [...prev.stats];
      newStats[index] = {
        ...newStats[index],
        [field]: value,
      };

      return {
        ...prev,
        stats: newStats,
      };
    });
  };

  const handleAddStat = () => {
    setFormData((prev) => ({
      ...prev,
      stats: [
        ...prev.stats,
        {
          value: "",
          label: "",
        },
      ],
    }));
  };

  const handleRemoveStat = (index) => {
    setFormData((prev) => ({
      ...prev,
      stats: prev.stats.filter((_, i) => i !== index),
    }));
  };

  // ---------------------------
  // Feature Handlers
  // ---------------------------

  const handleFeatureChange = (index, field, value) => {
    setFormData((prev) => {
      const newCapabilities = [...prev.features];

      newCapabilities[index] = {
        ...newCapabilities[index],
        [field]: value,
      };

      return {
        ...prev,
        features: newCapabilities,
      };
    });
  };

  const handleAddFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [
        ...prev.features,
        {
          title: "",
          desc: "",
        },
      ],
    }));
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  // ---------------------------
  // Process Handlers
  // ---------------------------

  const handleProcessChange = (index, field, value) => {
    setFormData((prev) => {
      const newProcess = [...prev.process];

      newProcess[index] = {
        ...newProcess[index],
        [field]: value,
      };

      return {
        ...prev,
        process: newProcess,
      };
    });
  };

  const handleAddProcess = () => {
    setFormData((prev) => ({
      ...prev,
      process: [
        ...prev.process,
        {
          step: "",
          title: "",
          desc: "",
        },
      ],
    }));
  };

  const handleRemoveProcess = (index) => {
    setFormData((prev) => ({
      ...prev,
      process: prev.process.filter((_, i) => i !== index),
    }));
  };

  // ---------------------------
  // FAQ Handlers
  // ---------------------------

  const handleFaqChange = (index, field, value) => {
    setFormData((prev) => {
      const newFaq = [...prev.faq];

      newFaq[index] = {
        ...newFaq[index],
        [field]: value,
      };

      return {
        ...prev,
        faq: newFaq,
      };
    });
  };

  const handleAddFaq = () => {
    setFormData((prev) => ({
      ...prev,
      faq: [
        ...prev.faq,
        {
          q: "",
          a: "",
        },
      ],
    }));
  };

  const handleRemoveFaq = (index) => {
    setFormData((prev) => ({
      ...prev,
      faq: prev.faq.filter((_, i) => i !== index),
    }));
  };

  // ---------------------------
  // Submit Form
  // ---------------------------

  const submitForm = async () => {
    try {
      setError(null);
      setLoading(true);

      let url;
      let method;

      if (mode === "add") {
        url = `${apiBaseUrl}/create`;
        method = "POST";
      } else {
        url = `${apiBaseUrl}/update/${service._id}`;
        method = "PUT";
      }

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

if (!response.ok) {
  console.log("Backend Error:", data);
  throw new Error(data.message || JSON.stringify(data));
}

console.log("Success:", data);

      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-6xl shadow-xl my-8">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === "add" ? "Add New Service" : "Edit Service"}
          </h2>

          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="flex justify-between items-center bg-red-100 border border-red-400 text-red-700 px-6 py-3 mt-2 border-b">
            <div>{error}</div>

            <div onClick={() => setError(null)} className="cursor-pointer">
              <RxCross2 />
            </div>
          </div>
        )}

        {/* Body */}
        <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="space-y-6">
            {/* ================= Basic Information ================= */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Basic Information
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slug *
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleFormChange}
                    placeholder="ai-development"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    placeholder="AI Development"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tagline
                </label>
                <input
                  type="text"
                  name="tagline"
                  value={formData.tagline}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Short Description
                </label>
                <textarea
                  name="shortDesc"
                  value={formData.shortDesc}
                  onChange={handleFormChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hero Image URL
                  </label>
                  <input
                    type="text"
                    name="heroImage"
                    value={formData.heroImage}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icon
                  </label>
                  <input
                    type="text"
                    name="icon"
                    value={formData.icon}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color
                  </label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleFormChange}
                    placeholder="#EC4899"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* ================= Statistics ================= */}

            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Statistics
              </h3>

              {formData.stats.map((stat, idx) => (
                <div
                  key={idx}
                  className="mb-4 p-4 bg-gray-50 rounded-lg border"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={stat.value}
                      placeholder="98%"
                      onChange={(e) =>
                        handleStatChange(idx, "value", e.target.value)
                      }
                      className="px-4 py-2 border rounded-lg"
                    />

                    <input
                      type="text"
                      value={stat.label}
                      placeholder="Customer Satisfaction"
                      onChange={(e) =>
                        handleStatChange(idx, "label", e.target.value)
                      }
                      className="px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <button
                    onClick={() => handleRemoveStat(idx)}
                    className="mt-3 w-full bg-red-100 hover:bg-red-200 text-red-600 py-2 rounded-lg"
                  >
                    Remove Statistic
                  </button>
                </div>
              ))}

              <button
                onClick={handleAddStat}
                className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
              >
                + Add Statistic
              </button>
            </div>

            {/* ================= Overview ================= */}

            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Overview
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Heading
                </label>

                <input
                  type="text"
                  value={formData.overview.heading}
                  onChange={(e) =>
                    handleOverviewChange("heading", e.target.value)
                  }
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Body
                </label>

                <textarea
                  rows="5"
                  value={formData.overview.body}
                  onChange={(e) => handleOverviewChange("body", e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
            </div>

            {/* ================= Features ================= */}

            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Features
              </h3>

              {formData.features.map((feature, idx) => (
                <div
                  key={idx}
                  className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title
                    </label>

                    <input
                      type="text"
                      value={feature.title}
                      onChange={(e) =>
                        handleFeatureChange(idx, "title", e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Feature title"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>

                    <textarea
                      rows="2"
                      value={feature.desc}
                      onChange={(e) =>
                        handleFeatureChange(idx, "desc", e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <button
                    onClick={() => handleRemoveFeature(idx)}
                    className="mt-3 w-full px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                  >
                    Remove Feature
                  </button>
                </div>
              ))}

              <button
                onClick={handleAddFeature}
                className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
              >
                + Add Feature
              </button>
            </div>

            {/* ================= Process ================= */}

            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Process
              </h3>

              {formData.process.map((item, idx) => (
                <div
                  key={idx}
                  className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Step
                      </label>

                      <input
                        type="text"
                        value={item.step}
                        onChange={(e) =>
                          handleProcessChange(idx, "step", e.target.value)
                        }
                        className="w-full px-4 py-2 border rounded-lg"
                        placeholder="01"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title
                      </label>

                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) =>
                          handleProcessChange(idx, "title", e.target.value)
                        }
                        className="w-full px-4 py-2 border rounded-lg"
                        placeholder="Requirement Analysis"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>

                    <textarea
                      rows="2"
                      value={item.desc}
                      onChange={(e) =>
                        handleProcessChange(idx, "desc", e.target.value)
                      }
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <button
                    onClick={() => handleRemoveProcess(idx)}
                    className="mt-3 w-full px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                  >
                    Remove Process
                  </button>
                </div>
              ))}

              <button
                onClick={handleAddProcess}
                className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
              >
                + Add Process
              </button>
            </div>

            {/* ================= Benefits ================= */}

            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Benefits
              </h3>

              {formData.benefits.map((item, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) =>
                      handleArrayFieldChange("benefits", idx, e.target.value)
                    }
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Benefit"
                  />

                  <button
                    onClick={() => handleRemoveArrayField("benefits", idx)}
                    className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                  >
                    Remove
                  </button>
                </div>
              ))}

              <button
                onClick={() => handleAddArrayField("benefits")}
                className="mt-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
              >
                + Add Benefit
              </button>
            </div>

            {/* ================= Industries ================= */}

            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Industries
              </h3>

              {formData.industries.map((industry, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={industry}
                    onChange={(e) =>
                      handleArrayFieldChange("industries", idx, e.target.value)
                    }
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Industry"
                  />

                  <button
                    onClick={() => handleRemoveArrayField("industries", idx)}
                    className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                  >
                    Remove
                  </button>
                </div>
              ))}

              <button
                onClick={() => handleAddArrayField("industries")}
                className="mt-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
              >
                + Add Industry
              </button>
            </div>

            {/* ================= Technologies ================= */}

            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Technologies
              </h3>

              {formData.technologies.map((tech, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tech}
                    onChange={(e) =>
                      handleArrayFieldChange(
                        "technologies",
                        idx,
                        e.target.value,
                      )
                    }
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Technology"
                  />

                  <button
                    onClick={() => handleRemoveArrayField("technologies", idx)}
                    className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                  >
                    Remove
                  </button>
                </div>
              ))}

              <button
                onClick={() => handleAddArrayField("technologies")}
                className="mt-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
              >
                + Add Technology
              </button>
            </div>

            {/* ================= FAQ ================= */}

            <div className="pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                FAQ (Optional)
              </h3>

              {formData.faq.map((item, idx) => (
                <div
                  key={idx}
                  className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Question
                    </label>

                    <input
                      type="text"
                      value={item.q}
                      onChange={(e) =>
                        handleFaqChange(idx, "q", e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Question"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Answer
                    </label>

                    <textarea
                      rows="3"
                      value={item.a}
                      onChange={(e) =>
                        handleFaqChange(idx, "a", e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <button
                    onClick={() => handleRemoveFaq(idx)}
                    className="mt-3 w-full px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                  >
                    Remove FAQ
                  </button>
                </div>
              ))}

              <button
                onClick={handleAddFaq}
                className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
              >
                + Add FAQ
              </button>
            </div>
          </div>
        </div>
        {/* Footer */}

        <div className="flex justify-end gap-4 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-semibold"
          >
            Cancel
          </button>

          <button
            onClick={submitForm}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {loading
              ? "Saving..."
              : mode === "add"
                ? "Create Service"
                : "Update Service"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceFormModal;
